import React, { useRef, useState } from "react";
import { API, http } from "@/lib/api";
import { Upload, FilePdf, Trash, Eye } from "@phosphor-icons/react";

/**
 * Private document uploader for licenses / insurance certs / completed W-9.
 * Accepts PDF + image. Stores path string in `value`.
 */
export default function DocumentUploader({
    label,
    value,             // storage path or empty
    onChange,          // (path or "") => void
    purpose,           // "license_state" | "license_county" | "license_local" | "insurance" | "w9"
    testidPrefix,
    accept = "application/pdf,image/*",
    maxBytes = 10 * 1024 * 1024,
}) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const token = typeof window !== "undefined" ? localStorage.getItem("br_token") : null;

    async function pick(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        setError("");
        if (f.size > maxBytes) {
            setError("File is larger than 10 MB.");
            return;
        }
        const fd = new FormData();
        fd.append("file", f);
        setUploading(true);
        try {
            const res = await fetch(`${API}/uploads/document?purpose=${encodeURIComponent(purpose)}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Upload failed");
            }
            const data = await res.json();
            onChange(data.storage_path);
        } catch (err) {
            setError(err.message || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    function viewFile() {
        if (!value) return;
        const url = `${API}/files/${value}?auth=${encodeURIComponent(token || "")}`;
        window.open(url, "_blank");
    }

    return (
        <div data-testid={`${testidPrefix}-wrap`}>
            <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
            {value ? (
                <div className="mt-2 flex items-center gap-2 bg-slate-950 border border-emerald-500/30 rounded-sm px-3 py-2.5">
                    <FilePdf size={18} weight="duotone" className="text-emerald-300 shrink-0" />
                    <span className="text-sm text-slate-300 truncate flex-1">Uploaded</span>
                    <button type="button" onClick={viewFile} data-testid={`${testidPrefix}-view`}
                        className="text-xs text-gold hover:underline inline-flex items-center gap-1">
                        <Eye size={12} /> View
                    </button>
                    <button type="button" onClick={() => onChange("")} data-testid={`${testidPrefix}-remove`}
                        className="text-xs text-slate-500 hover:text-red-400 inline-flex items-center gap-1">
                        <Trash size={12} /> Remove
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testidPrefix}-add`}
                    className="mt-2 w-full bg-slate-950 border border-dashed border-white/15 rounded-sm px-3 py-3 text-sm text-slate-400 hover:border-gold hover:text-gold transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                    <Upload size={14} weight="bold" />
                    {uploading ? "Uploading…" : "Upload PDF or image"}
                </button>
            )}
            <input
                ref={fileRef} type="file" accept={accept} className="hidden"
                onChange={pick}
                data-testid={`${testidPrefix}-input`}
            />
            {error && <p data-testid={`${testidPrefix}-error`} className="text-xs text-red-400 mt-1">{error}</p>}
        </div>
    );
}
